import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';
import './AdminAgents.css';
import './AdminWeeklyEmails.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const formatDate = (value) => (value ? new Date(value).toLocaleString() : 'Never');

const AdminWeeklyEmails = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sendingSample, setSendingSample] = useState(false);
  const [running, setRunning] = useState(false);
  const [runLimit, setRunLimit] = useState(5);
  const [lastBatch, setLastBatch] = useState(null);

  const loadSettings = useCallback(async () => {
    const res = await fetch(`${API_URL}/mealplan/weekly-email/admin/settings`, {
      headers: authHeaders()
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Failed to load email settings');
    }
    return res.json();
  }, []);

  useEffect(() => {
    loadSettings()
      .then(setSettings)
      .catch((error) => {
        console.error(error);
        toast.error(error.message);
      })
      .finally(() => setLoading(false));
  }, [loadSettings]);

  const toggleOutbound = async (outboundEnabled) => {
    const previous = settings?.outboundEnabled;
    setSettings((prev) => ({ ...prev, outboundEnabled }));
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/mealplan/weekly-email/admin/settings`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ outboundEnabled })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update setting');
      setSettings(data);
      toast.success(outboundEnabled
        ? 'Weekly meal plan emails are ON for opted-in users'
        : 'Weekly meal plan emails are OFF — no outbound sends to signed-in users');
    } catch (error) {
      setSettings((prev) => ({ ...prev, outboundEnabled: previous }));
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const sendSampleToMe = async () => {
    setSendingSample(true);
    try {
      const res = await fetch(`${API_URL}/mealplan/weekly-email/send-test`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to send sample');
      if (data.sent) {
        toast.success(`Sample emailed to ${data.to}`);
      } else {
        toast.success(data.reason || 'Preview generated, but email is not configured.');
        if (data.html) {
          const preview = window.open('', '_blank');
          if (preview) {
            preview.document.write(data.html);
            preview.document.close();
          }
        }
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSendingSample(false);
    }
  };

  const runBatch = async () => {
    if (!settings?.outboundEnabled) {
      toast.error('Turn outbound email ON before running a batch send.');
      return;
    }
    setRunning(true);
    try {
      const res = await fetch(`${API_URL}/mealplan/weekly-email/run`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ limit: Number(runLimit) || 5 })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to run weekly emails');
      setLastBatch(data);
      const refreshed = await loadSettings();
      setSettings(refreshed);
      if (data.skipped) {
        toast.error(data.reason || 'Batch send was skipped');
      } else {
        toast.success(`Attempted ${data.attempted}, sent ${data.sent}`);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return <div className="agents-loading">Loading weekly email settings…</div>;
  }

  return (
    <div className="admin-weekly-emails">
      <div className="agents-intro">
        <h2>Weekly meal plan emails</h2>
        <p>
          Users can opt in from signup or Profile. This master switch controls whether those
          emails actually go out. Leave it off while you test a sample to your own inbox.
        </p>
      </div>

      <div className="agent-card">
        <div className="agent-card-header">
          <div>
            <span className="agent-step-label">Outbound</span>
            <h3>Send weekly emails to opted-in users</h3>
            <p className="agent-card-desc">
              Off by default. When on, the Sunday 14:00 UTC job emails everyone who opted in.
              Sample emails to your own account still work while this is off.
            </p>
          </div>
          <label className="agent-switch" title={saving ? 'Saving…' : 'Toggle outbound weekly emails'}>
            <input
              type="checkbox"
              checked={!!settings?.outboundEnabled}
              disabled={saving}
              onChange={(e) => toggleOutbound(e.target.checked)}
            />
            <span className="agent-slider" />
          </label>
        </div>

        <div className="weekly-email-status-grid">
          <div>
            <span className="weekly-email-stat-label">Status</span>
            <strong className={settings?.outboundEnabled ? 'weekly-email-on' : 'weekly-email-off'}>
              {settings?.outboundEnabled ? 'Outbound ON' : 'Outbound OFF'}
            </strong>
          </div>
          <div>
            <span className="weekly-email-stat-label">Opted-in users</span>
            <strong>{settings?.optedInCount ?? 0}</strong>
          </div>
          <div>
            <span className="weekly-email-stat-label">Schedule</span>
            <strong>{settings?.scheduler?.schedule || 'Sundays 14:00 UTC'}</strong>
          </div>
          <div>
            <span className="weekly-email-stat-label">Weekly menu model</span>
            <strong>{settings?.model || 'gemini-3.7-flash'}</strong>
          </div>
          <div>
            <span className="weekly-email-stat-label">Last batch</span>
            <strong>{formatDate(settings?.lastRunAt)}</strong>
          </div>
        </div>

        {settings?.lastRunReason && (
          <p className="weekly-email-last-reason">{settings.lastRunReason}</p>
        )}
        {settings?.lastRunAt && !settings.lastRunReason && (
          <p className="weekly-email-last-reason">
            Last {settings.lastRunTrigger || 'run'}: attempted {settings.lastRunAttempted}, sent {settings.lastRunSent}
          </p>
        )}

        <div className="weekly-email-actions">
          <button
            type="button"
            className="agent-run-btn"
            onClick={sendSampleToMe}
            disabled={sendingSample}
          >
            {sendingSample ? 'Sending sample…' : 'Send a sample to my inbox'}
          </button>
          <div className="weekly-email-run-now">
            <label>
              Max users
              <input
                type="number"
                min="1"
                max="200"
                value={runLimit}
                onChange={(e) => setRunLimit(e.target.value)}
              />
            </label>
            <button
              type="button"
              className="weekly-email-batch-btn"
              onClick={runBatch}
              disabled={running || !settings?.outboundEnabled}
              title={settings?.outboundEnabled ? 'Send to opted-in users now' : 'Turn outbound ON first'}
            >
              {running ? 'Running…' : 'Run now'}
            </button>
          </div>
        </div>
      </div>

      {lastBatch?.results?.length > 0 && (
        <div className="agent-card">
          <h3>Last run results</h3>
          <table className="weekly-email-results">
            <thead>
              <tr>
                <th>Email</th>
                <th>Sent</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {lastBatch.results.map((row) => (
                <tr key={row.userId || row.email}>
                  <td>{row.email || row.userId}</td>
                  <td>{row.sent ? 'Yes' : 'No'}</td>
                  <td>{row.reason || `${row.meals || 0} meals`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminWeeklyEmails;
